import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { GREEN } from '../../constants';
import { fmtMoneySmart } from '../../utils/formatters';
import { ChartFrame } from '../charts/ChartFrame';
import usePnlChartConfig from '../charts/presets/usePnlChartConfig';

// If the correct export is 'IDailyData', update as follows:
// If 'DailyData' is exported from '../../types/dashboard', use:
// Define DailyData interface locally since it's not exported from '../../types/dashboard'
interface DailyData {
  date: string;
  cum: number;
}

// If not, define it here as a fallback:
//
// interface DailyData {
//   date: string;
//   cum: number;
// }

// Or, if the export is 'DashboardDailyData', use:
//
// import type { DashboardDailyData as DailyData } from '../../types/dashboard';
//
// If 'DailyData' is missing, add its definition to '../../types/dashboard.ts':
//
// export interface DailyData {
//   date: string;
//   cum: number;
//   // add other fields as needed
// }

interface CumulativePnlWidgetProps {
  dailyData: DailyData[];
  isDark: boolean;
}

// Use shared P&L chart config hook for consistent settings

export const CumulativePnlWidget: React.FC<CumulativePnlWidgetProps> = React.memo(({ dailyData, isDark }) => {
  const hasData = Array.isArray(dailyData) && dailyData.length > 0;

  const {
    dataMemo,
    ticksMemo,
    domainMemo,
    chartMargin,
    yAxisWidth,
    xTickStyle,
    axisLineProps,
    xTickFormatter,
    gridColor,
    lineProps,
    referenceLineProps,
  } = usePnlChartConfig(dailyData, isDark);

  // Measure header left so we can align Y-axis labels with the header's left edge
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [yLabelX, setYLabelX] = useState<number | null>(null);

  const renderCustomTooltip = useCallback((props: any) => {
    if (props && props.active && props.payload && props.payload.length) {
      const data = props.payload[0];
      const ts = props.label;
      const d = new Date(ts);
      const dateStr = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;

      return (
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-2 text-sm">
          <p className="font-semibold text-zinc-600 dark:text-zinc-300">{dateStr}</p>
          <p style={{ color: data.payload?.fill || data.color || GREEN }}>
            Cumulative P&L: {fmtMoneySmart(data.value)}
          </p>
        </div>
      );
    }
    return null;
  }, []);

  useLayoutEffect(() => {
    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const widget = el.closest('.widget-card') as HTMLElement | null;
      const header = widget?.querySelector('h3, h2') as HTMLElement | null;
      const chartRect = el.getBoundingClientRect();
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const offset = Math.round(headerRect.left - chartRect.left);
        const inset = 0;
        setYLabelX(Math.max(0, offset + inset));
      } else {
        setYLabelX(8);
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const renderYTick = useCallback(({ x, y, payload }: any) => {
    const v = payload?.value as number;
    const txt = fmtMoneySmart(v);
    const xPos = (typeof yLabelX === 'number') ? yLabelX : (x + 4);
    const fill = isDark ? '#9ca3af' : '#374151';

    // If this tick value is the maximum value in the chart data, nudge it up
    // by 6px so it doesn't collide with the top edge/overflow.
    const maxVal = (Array.isArray(dataMemo) && dataMemo.length)
      ? Math.max(...dataMemo.map(d => (typeof (d as any).value === 'number' ? (d as any).value : ((d as any).cum ?? 0))))
      : null;
    const isTopTick = (typeof v === 'number' && typeof maxVal === 'number' && Math.abs(v - maxVal) < 1e-9);
    const yPos = isTopTick ? (y - 6) : y;

    return (
      <text x={xPos} y={yPos} fill={fill} fontSize={12} textAnchor="start" dominantBaseline="middle">
        {txt}
      </text>
    );
  }, [isDark, yLabelX, dataMemo]);

  return (
    <div className="h-full flex flex-col" ref={containerRef}>
      {!hasData ? (
        <div className="h-full flex items-center justify-center rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          Import Trades to see the widget
        </div>
      ) : (
      <div data-testid="cumulative-pnl-chart-container" className="flex-1 pr-1">
        <ChartFrame>
          {({ width, height }: { width: number; height: number }) => (
            <LineChart width={width} height={height} data={dataMemo} margin={chartMargin}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <YAxis
                dataKey="value"
                type="number"
                tickFormatter={(v: number) => fmtMoneySmart(v)}
                tick={renderYTick as any}
                axisLine={true}
                tickLine={true}
                width={yAxisWidth}
              />
              <XAxis
                dataKey="ts"
                type="number"
                domain={domainMemo}
                ticks={ticksMemo}
                interval={0}
                allowDecimals={false}
                tickMargin={8}
                height={28}
                minTickGap={0}
                tickLine={true}
                axisLine={axisLineProps}
                tick={xTickStyle as any}
                tickFormatter={xTickFormatter}
              />
              <Tooltip isAnimationActive={false} content={renderCustomTooltip as any} />
              <ReferenceLine y={0} stroke={referenceLineProps.stroke} strokeWidth={referenceLineProps.strokeWidth} strokeDasharray={referenceLineProps.strokeDasharray} />
              <Line type="monotone" dataKey="value" dot={false} isAnimationActive={false} animationDuration={0} stroke={lineProps.stroke} strokeWidth={lineProps.strokeWidth} strokeLinecap={lineProps.strokeLinecap} />
            </LineChart>
          )}
        </ChartFrame>
      </div>
      )}
    </div>
  );
}, (prev, next) => {
  if (prev.isDark !== next.isDark) return false;
  const a = prev.dailyData, b = next.dailyData;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const la = a[a.length - 1]?.date;
  const lb = b[b.length - 1]?.date;
  return la === lb;
});
